import Experience from '../Experience.js'
import * as THREE from 'three';

import videoVertexShader from '../shaders/video/vertex.vert'
import videoFragmentShader from '../shaders/video/fragment.frag'

import UrlParams from '@/core/UrlParams';

export default class LiveFeed
{
    constructor()
    {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.resources = this.experience.resources
        this.sizes = this.experience.sizes
        this.gui = this.experience.gui

        this.videoTexture = new THREE.VideoTexture( this.experience.video );
        
        this.luminanceShader = {
            luminanceValue : 10,
            edgeDelta : 0.002,
            luminanceDivider : 10.8,
            luminanceInEdgeMultiplier : 3.9,
            blur : 5.0,
            edgeClamp : 3.0,
            edgeClampBounds : 1.0,
            edgeMultiplier : 0.24,
            hueMultiplier : 1.1,
            luminanceMultiplier : 1.1,
            saturationMultiplier : 0.8,
        }

        this.sepiaEffect = {
            isSepia : false,
            rr : 0.393,
            rg : 0.769,
            rb : 0.189,
            gr : 0.349,
            gg : 0.686,
            gb : 0.168,
            br : 0.272,
            bg : 0.534,
            bb : 0.131,
        }

        this.multipliedTexture = { size : 2.0 }

        this.lineEffect = {
            lineThreshold : 0.85,
        }
    }

    init(){
        this.setGeometry()
        this.setTextures()
        this.setMaterial()
        this.setMesh()

        const isDebug = UrlParams.getBool('debug');
        if(isDebug && process.env.VUE_APP_ENVCONFIG === "development") {
            this.setGUI();
        }
    }

    setGeometry()
    {
        this.geometry = new THREE.PlaneGeometry(2, 2, 1, 1)
    }

    setTextures()
    {
        this.texture = this.resources.items.posterBackgroundTexture
    }

    setMaterial()
    {
        const videoShader = {
            uniforms:
            {
                tWebcamFeed: { value: this.videoTexture },
                tMultipliedTexture : { value: null },
                uMultipliedTextureSize : { value: this.multipliedTexture.size },
                uTime: { value: 0 },
                uAspectRatio: { value: this.sizes.width / this.sizes.height },
                uPixelRatio: { value: this.sizes.pixelRatio },
                uResolution: { value: new THREE.Vector2(1080,1080) },
        
                uLuminanceValues : { value : this.luminanceShader.luminanceValue},
                uEdgeDelta : { value : this.luminanceShader.edgeDelta },
                uLuminanceDivider : { value : this.luminanceShader.luminanceDivider },
                uLuminanceInEdgeMultiplier : { value : this.luminanceShader.luminanceInEdgeMultiplier },
                uBlur : { value : this.luminanceShader.blur },
                uEdgeClamp : { value : this.luminanceShader.edgeClamp },
                uEdgeClampBounds : { value : this.luminanceShader.edgeClampBounds },
                uEdgeMultiplier : { value : this.luminanceShader.edgeMultiplier },
                uHueMultiplier : { value : this.luminanceShader.hueMultiplier },
                uLuminanceMultiplier : { value : this.luminanceShader.luminanceMultiplier },
                uSaturation : { value : this.luminanceShader.saturationMultiplier },
        
                uIsSepia : { value : this.sepiaEffect.isSepia },
                uSepiaRR : { value : this.sepiaEffect.rr },
                uSepiaRG : { value : this.sepiaEffect.rg },
                uSepiaRB : { value : this.sepiaEffect.rb },
                uSepiaGR : { value : this.sepiaEffect.gr },
                uSepiaGG : { value : this.sepiaEffect.gg },
                uSepiaGB : { value : this.sepiaEffect.gb },
                uSepiaBR : { value : this.sepiaEffect.br },
                uSepiaBG : { value : this.sepiaEffect.bg },
                uSepiaBB : { value : this.sepiaEffect.bb },

                uLineThreshold : { value : this.lineEffect.lineThreshold },
        
        
            },
            vertexShader: videoVertexShader,
            fragmentShader: videoFragmentShader
        }
        
        const videoShaderMaterial = new THREE.ShaderMaterial(videoShader)

        videoShaderMaterial.uniforms.tMultipliedTexture.value = this.resources.items.toileTexture

        this.material = videoShaderMaterial
    }

    setMesh()
    {
        this.mesh = new THREE.Mesh(this.geometry, this.material)
        this.mesh.position.y = -1.
        this.mesh.position.z = -0.5
        this.scene.add(this.mesh)
        this.mesh.visible = true
    }

    setGUI()
    {
        const luminanceFolder = this.gui.addFolder('Luminance')
        luminanceFolder.add(this.luminanceShader, 'luminanceValue').min(0).max(25).step(0.01).name('Luminance')
        .onChange(() => {
            this.material.uniforms.uLuminanceValues.value = this.luminanceShader.luminanceValue
        })

        luminanceFolder.add(this.luminanceShader, 'edgeDelta').min(0).max(0.01).step(0.0001).name('Edge Delta')
        .onChange(() => {
            this.material.uniforms.uEdgeDelta.value = this.luminanceShader.edgeDelta
        })

        luminanceFolder.add(this.luminanceShader, 'luminanceDivider').min(0.1).max(15).step(0.1).name('Luminance Divider')
        .onChange(() => {
            this.material.uniforms.uLuminanceDivider.value = this.luminanceShader.luminanceDivider
        })

        luminanceFolder.add(this.luminanceShader, 'luminanceInEdgeMultiplier').min(3).max(5).step(0.001).name('Luminance In Edge Multiplier')
        .onChange(() => {
            this.material.uniforms.uLuminanceInEdgeMultiplier.value = this.luminanceShader.luminanceInEdgeMultiplier
        })

        luminanceFolder.add(this.luminanceShader, 'blur').min(0.1).max(10).step(0.1).name('Blur')
        .onChange(() => {
            this.material.uniforms.uBlur.value = this.luminanceShader.blur
        })

        luminanceFolder.add(this.luminanceShader, 'edgeClamp').min(0.1).max(10).step(0.1).name('Edge Clamp')
        .onChange(() => {
            this.material.uniforms.uEdgeClamp.value = this.luminanceShader.edgeClamp
        })

        luminanceFolder.add(this.luminanceShader, 'edgeClampBounds').min(0.1).max(10).step(0.01).name('Edge Clamp Bounds')
        .onChange(() => {
            this.material.uniforms.uEdgeClampBounds.value = this.luminanceShader.edgeClampBounds
        })

        luminanceFolder.add(this.luminanceShader, 'edgeMultiplier').min(0.0).max(1).step(0.001).name('Edge Multiplier')
        .onChange(() => {
            this.material.uniforms.uEdgeMultiplier.value = this.luminanceShader.edgeMultiplier
        })

        luminanceFolder.add(this.luminanceShader, 'hueMultiplier').min(0.0).max(5).step(0.01).name('Hue Multiplier')
        .onChange(() => {
            this.material.uniforms.uHueMultiplier.value = this.luminanceShader.hueMultiplier
        })

        luminanceFolder.add(this.luminanceShader, 'luminanceMultiplier').min(0.0).max(5).step(0.01).name('Luminance Multiplier')
        .onChange(() => {
            this.material.uniforms.uLuminanceMultiplier.value = this.luminanceShader.luminanceMultiplier
        })

        luminanceFolder.add(this.luminanceShader, 'saturationMultiplier').min(0.0).max(5).step(0.01).name('Saturation Multiplier')
        .onChange(() => {
            this.material.uniforms.uSaturation.value = this.luminanceShader.saturationMultiplier
        })

        const sepiaFolder = this.gui.addFolder('Sepia')
        sepiaFolder.add(this.sepiaEffect, 'isSepia').name('Is Sepia').onChange(() => {
            this.material.uniforms.uIsSepia.value = this.sepiaEffect.isSepia
        })
        sepiaFolder.add(this.sepiaEffect, 'rr').min(0).max(2).step(0.001).name('rr')
        .onChange(() => {
            this.material.uniforms.uSepiaRR.value = this.sepiaEffect.rr
        })
        sepiaFolder.add(this.sepiaEffect, 'rg').min(0).max(2).step(0.001).name('rg')
        .onChange(() => {
            this.material.uniforms.uSepiaRG.value = this.sepiaEffect.rg
        })
        sepiaFolder.add(this.sepiaEffect, 'rb').min(0).max(2).step(0.001).name('rb')
        .onChange(() => {
            this.material.uniforms.uSepiaRB.value = this.sepiaEffect.rb
        })
        sepiaFolder.add(this.sepiaEffect, 'gr').min(0).max(2).step(0.001).name('gr')
        .onChange(() => {
            this.material.uniforms.uSepiaGR.value = this.sepiaEffect.gr
        })
        sepiaFolder.add(this.sepiaEffect, 'gg').min(0).max(2).step(0.001).name('gg')
        .onChange(() => {
            this.material.uniforms.uSepiaGG.value = this.sepiaEffect.gg
        })
        sepiaFolder.add(this.sepiaEffect, 'gb').min(0).max(2).step(0.001).name('gb')
        .onChange(() => {
            this.material.uniforms.uSepiaGB.value = this.sepiaEffect.gb
        })
        sepiaFolder.add(this.sepiaEffect, 'br').min(0).max(2).step(0.001).name('br')
        .onChange(() => {
            this.material.uniforms.uSepiaBR.value = this.sepiaEffect.br
        })
        sepiaFolder.add(this.sepiaEffect, 'bg').min(0).max(2).step(0.001).name('bg')
        .onChange(() => {
            this.material.uniforms.uSepiaBG.value = this.sepiaEffect.bg
        })
        sepiaFolder.add(this.sepiaEffect, 'bb').min(0).max(2).step(0.001).name('bb')
        .onChange(() => {
            this.material.uniforms.uSepiaBB.value = this.sepiaEffect.bb
        })

        const multipliedTextureFolder = this.gui.addFolder('Multiplied Texture')
        multipliedTextureFolder.add(this.multipliedTexture, 'size').min(0.1).max(5).step(0.01).name('Multiplied Texture Size')
        .onChange(() => {
            this.material.uniforms.uMultipliedTextureSize.value = this.multipliedTexture.size
        })

        const lineEffectFolder = this.gui.addFolder('Line Effect')
        lineEffectFolder.add(this.lineEffect, 'lineThreshold').min(0.1).max(1.5).step(0.01).name('Line Threshold')
        .onChange(() => {
            this.material.uniforms.uLineThreshold.value = this.lineEffect.lineThreshold
        })

        luminanceFolder.close()
        sepiaFolder.close()
        multipliedTextureFolder.close()
        lineEffectFolder.close()
    }

}